import apiClient from './apiClient';

// Authentication API Services
export const authAPI = {
  // Login user
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/user/login', credentials);
      // Your backend returns { success, message, token, user }
      if (response.data.success && response.data.token) {
        sessionStorage.setItem('bciis_auth_token', response.data.token);
        sessionStorage.setItem('bciis_user', JSON.stringify(response.data.user));
        
        return response.data;
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Login failed');
    }
  },

  // Register new user
  signup: async (userData) => {
    try {
      const response = await apiClient.post('/user/register', userData);
      // Your backend returns { success, message, userId } - no token or user object
      if (response.data.success) {
        // Create a user object for the frontend to use
        const user = {
          id: response.data.userId,
          username: userData.username,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          role: userData.role || 'user'
        };
        
        // Store user data (no token from your backend)
        sessionStorage.setItem('bciis_user', JSON.stringify(user));
        
        return {
          success: true,
          message: response.data.message,
          user: user
        };
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Registration failed');
    }
  },

  // Logout user
  logout: async () => {
    try {
      await apiClient.post('/user/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      // Always clear session storage
      sessionStorage.removeItem('bciis_auth_token');
      sessionStorage.removeItem('bciis_user');
      sessionStorage.removeItem('bciis_current_page');
    }
  },

  // Verify token validity
  verifyToken: async () => {
    try {
      const response = await apiClient.post('/auth/authCheck', {
        session: sessionStorage.getItem('bciis_auth_token')
      });
      return response.data;
    } catch (error) {
      throw new Error('Token verification failed');
    }
  },

  // Refresh token (not implemented on backend, kept for future use)
  refreshToken: async () => {
    try {
      const response = await apiClient.post('/user/login');
      if (response.data.token) {
        sessionStorage.setItem('bciis_auth_token', response.data.token);
      }
      return response.data;
    } catch (error) {
      throw new Error('Token refresh failed');
    }
  },

  // Get current user profile (stored locally; no dedicated backend GET profile endpoint)
  getProfile: async () => {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
      return { success: true, user: currentUser };
    } catch (error) {
      throw new Error('Failed to fetch profile');
    }
  },

  // Forgot password - request reset code
  forgotPassword: async (email) => {
    try {
      const response = await apiClient.post('/user/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to send reset code');
    }
  },

  // Verify reset code
  verifyResetCode: async (email, resetCode) => {
    try {
      const response = await apiClient.post('/user/verify-reset-code', { email, resetCode });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Invalid or expired reset code');
    }
  },

  // Reset password using verified code
  resetPassword: async (email, resetCode, newPassword) => {
    try {
      const response = await apiClient.post('/user/reset-password', { email, resetCode, newPassword });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to reset password');
    }
  },

  // Update user profile
  updateProfile: async (profileData) => {
    try {
      // Get user ID from session storage or profileData
      const currentUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
      const userId = profileData.id || profileData.user_id || currentUser.id || currentUser.user_id;
      if (!userId) {
        throw new Error('User ID not found. Please log in again.');
      }

      const requestData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        organization: profileData.organization,
        municipality: profileData.municipality,
        avatar: profileData.avatar
      };

      // Log avatar information
      // Avatar data included in request
      const response = await apiClient.put(`/user/${userId}/profile`, requestData);
      // Update session storage with new user data
      if (response.data.success) {
        const updatedUser = {
          ...currentUser,
          ...profileData
        };
        sessionStorage.setItem('bciis_user', JSON.stringify(updatedUser));
        return { success: true, user: updatedUser };
      } else {
      }
      
      return response.data;
    } catch (error) {
      
      // Check for specific error types
      if (error.response?.status === 413) {
        throw new Error('Profile picture is too large. Please use a smaller image.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error('Failed to update profile. Please try again.');
      }
    }
  },

  // Change password
  changePassword: async (passwordData) => {
    try {
      const response = await apiClient.put(`/user/${passwordData.userId}/password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to change password');
    }
  },

  // Upload user avatar (avatar is stored as base64 via updateProfile; kept for future use)
  uploadAvatar: async (avatarFile) => {
    try {
      const formData = new FormData();
      formData.append('avatar', avatarFile);
      
      const response = await apiClient.post('/auth/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update session storage with new avatar URL
      const currentUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
      if (response.data.avatarUrl) {
        currentUser.avatar = response.data.avatarUrl;
        sessionStorage.setItem('bciis_user', JSON.stringify(currentUser));
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload avatar');
    }
  }
};

const normalizeAdminGender = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'm' || normalized === 'male') return 'm';
  if (normalized === 'f' || normalized === 'female') return 'f';
  return '';
};

// Admin API Services
export const adminAPI = {
  // Admin login
  login: async (credentials) => {
    try {
      // Backend admin login uses POST with JSON body
      const response = await apiClient.post('/admins/login', {
        username: credentials.username,
        password: credentials.password
      });
      
      // Backend returns { success, message, token, user }
      if (response.data && response.data.success && response.data.token) {
        const adminData = response.data.user;
        
        // Create a standardized user object
        const user = {
          id: adminData.id,
          username: adminData.username || credentials.username,
          fullname: adminData.fullname || '',
          gender: normalizeAdminGender(adminData.gender ?? adminData.Gender) || 'm',
          role: 'admin',
          avatar: adminData.avatar || null
        };
        
        // Store auth data with real JWT from backend
        sessionStorage.setItem('bciis_auth_token', response.data.token);
        sessionStorage.setItem('bciis_user', JSON.stringify(user));
        
        return { 
          success: true, 
          message: 'Login successful', 
          token: response.data.token, 
          user: user 
        };
      } else {
        throw new Error('Invalid admin credentials');
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Admin login failed');
    }  },

  // Update admin profile
  updateProfile: async (adminId, profileData) => {
    try {
      const response = await apiClient.put(`/admins/${adminId}/profile`, profileData);
      if (response.data.success) {
        // Update stored user data in sessionStorage
        const storedUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
        const updatedUser = {
          ...storedUser,
          fullname: profileData.fullname,
          gender: normalizeAdminGender(profileData.gender) || 'm',
          avatar: profileData.avatar
        };
        sessionStorage.setItem('bciis_user', JSON.stringify(updatedUser));
        
        return response.data;
      } else {
        throw new Error(response.data.error || 'Failed to update admin profile');
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Failed to update admin profile');
    }  }
  ,

  // Change admin password
  changePassword: async (adminId, passwordData) => {
    try {
      const response = await apiClient.put(`/admins/${adminId}/password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to change password');
    }
  }
};

// Admin Application API Services
export const adminApplicationAPI = {
  // Submit new admin application
  apply: async (applicationData) => {
    try {
      const response = await apiClient.post('/admin-applications/apply', applicationData);
      if (response.data.error === false) {
        return {
          success: true,
          message: response.data.message,
          applicationId: response.data.applicationId
        };
      } else {
        throw new Error(response.data.message || 'Application submission failed');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to submit admin application');
    }
  },

  // Get all pending applications (Admin only)
  getPending: async () => {
    try {
      const response = await apiClient.get('/admin-applications/pending');
      
      if (response.data.error === false) {
        return {
          success: true,
          applications: response.data.applications || []
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch pending applications');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch pending applications');
    }
  },

  // Get all applications (Admin only)
  getAll: async () => {
    try {
      const response = await apiClient.get('/admin-applications/all');
      
      if (response.data.error === false) {
        return {
          success: true,
          applications: response.data.applications || []
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch applications');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch applications');
    }
  },

  // Get application by ID
  getById: async (applicationId) => {
    try {
      const response = await apiClient.get(`/admin-applications/${applicationId}`);
      
      if (response.data.error === false) {
        return {
          success: true,
          application: response.data.application
        };
      } else {
        throw new Error(response.data.message || 'Application not found');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch application');
    }
  },

  // Approve an admin application
  approve: async (applicationId, reviewedBy) => {
    try {
      const response = await apiClient.put(`/admin-applications/${applicationId}/approve`, {
        reviewedBy
      });
      
      if (response.data.error === false) {
        return {
          success: true,
          message: response.data.message,
          adminId: response.data.adminId
        };
      } else {
        throw new Error(response.data.message || 'Approval failed');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to approve application');
    }
  },

  // Reject an admin application
  reject: async (applicationId, reviewedBy) => {
    try {
      const response = await apiClient.put(`/admin-applications/${applicationId}/reject`, {
        reviewedBy
      });
      
      if (response.data.error === false) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        throw new Error(response.data.message || 'Rejection failed');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to reject application');
    }
  }
};

// Processors API Services
export const processorsAPI = {
  // Get all processors with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await apiClient.get(`/processors/getall${queryParams ? `?${queryParams}` : ''}`);
      return response.data;
    } catch (error) {
      // Log full axios error when available to aid debugging (500, ETIMEDOUT, etc.)
      try {
      } catch (logErr) {
      }

      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch processors');
    }
  },

  // Get processor by ID
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/processors/businesses/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch processor');
    }
  },

  // Create new processor
  create: async (processorData) => {
    try {
      const response = await apiClient.post('/processors/insert_business', processorData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create processor');
    }
  },

  // Update processor
  update: async (id, processorData) => {
    try {
      const response = await apiClient.put(`/processors/businesses/${id}`, processorData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update processor');
    }
  },

  // Delete processor
  delete: async (id) => {
    // Backend mounts processors router at /processors (from server.js: app.use("/processors", Processors))
    // The delete route is router.delete("/businesses/:id", ...) in the processors router
    // Full path: /processors/businesses/:id
    try {
      const response = await apiClient.delete(`/processors/businesses/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      // Extract backend error message
      const serverMsg = error.response?.data?.error || error.response?.data?.message;
      throw new Error(serverMsg || error.message || 'Failed to delete processor');
    }
  },

  // Update processor status (admin only)
  updateStatus: async (id, status) => {
    try {
      const response = await apiClient.patch(`/processors/${id}/status`, { statusOfBusiness: status });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update processor status');
    }
  },

  // Bulk delete processors - NOTE: no backend route exists for this yet
  // bulkDelete: async (ids) => { ... },

  // Get processors by municipality
  getByMunicipality: async (municipality) => {
    try {
      const response = await apiClient.get(`/processors/municipality/${municipality}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch processors by municipality');
    }
  },

  // Search processors
  search: async (searchTerm) => {
    try {
      const response = await apiClient.get(`/processors/search?q=${encodeURIComponent(searchTerm)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search processors');
    }
  },

  // Bulk import comprehensive processors
  bulkImportComprehensive: async (processors) => {
    try {
      const response = await apiClient.post('/processors/bulk-import-comprehensive', { processors });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Import failed');
    }
  }
};

// Farmers API Services
// NOTE: these endpoints map to the backend routes you provided. The router appears to expose
// routes like: GET /get_farmer, GET /search?q=, POST /insert, PUT /update/:id, DELETE /delete/:id
// which are assumed to be mounted under the "farmers" router path on the server (e.g. /farmers/get_farmer).
export const farmersAPI = {
  // Get all farmers (calls your stored procedure endpoint)
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await apiClient.get(`/farmers/get_farmer${queryParams ? `?${queryParams}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch farmers');
    }
  },

  // Search farmers using backend search endpoint
  search: async (searchTerm) => {
    try {
      const response = await apiClient.get(`/farmers/search?q=${encodeURIComponent(searchTerm)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search farmers');
    }
  },

  // Insert a farmer (calls your /insert route)
  create: async (farmerData) => {
    try {
      // Sanitize payload: replace any `undefined` values with `null` so the MySQL driver
      // doesn't reject bind parameters. Also strip out any keys with value `undefined` in
      // nested objects by converting them to null or removing them as appropriate.
      // sanitize nested objects by removing undefined keys; keep null values.
      // For the top-level SP parameters we want explicit nulls for missing scalars,
      // but nested objects should be omitted or only contain provided fields.
      const sanitize = (v) => {
        if (v === undefined) return undefined; // indicate absence
        if (v === null) return null;
        if (Array.isArray(v)) return v.map(sanitize);
        if (typeof v === 'object') {
          const out = {};
          for (const [k, val] of Object.entries(v)) {
            const s = sanitize(val);
            // only include keys that are not undefined
            if (s !== undefined) out[k] = s;
          }
          return Object.keys(out).length ? out : undefined;
        }
        return v;
      };

      const replaceTopLevelUndefinedWithNull = (obj) => {
        // Ensure the main stored-procedure scalar params exist and are null when absent.
        const expectedTopLevel = [
          'lastname','firstname','middleInitial','respondent','sitioPurok',
          'barangay','municipality','age','gender','civilStatus','education','household','occupation'
        ];

        const out = {};
        const src = (obj && typeof obj === 'object' && !Array.isArray(obj)) ? obj : {};

        // Copy provided keys, converting any accidental undefined to null
        for (const [k, v] of Object.entries(src)) {
          out[k] = (v === undefined) ? null : v;
        }

        // Ensure expected top-level keys are present (set to null if missing)
        for (const k of expectedTopLevel) {
          if (!(k in out)) out[k] = null;
        }

        // Also preserve any nested objects that were present (incomeData, membershipData, etc.)
        return out;
      };

      const partiallySanitized = sanitize(farmerData);
      const sanitized = replaceTopLevelUndefinedWithNull(partiallySanitized || {});
      
      const response = await apiClient.post('/farmers/insert', sanitized);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create farmer');
    }
  },

  // Update farmer (calls your /update/:id route)
  update: async (id, farmerData) => {
    try {
      const response = await apiClient.put(`/farmers/update/${encodeURIComponent(id)}`, farmerData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update farmer');
    }
  },

  // Delete farmer (calls your /delete/:id route)
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/farmers/delete/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      // Prefer the `error` field many backend routes return, fall back to `message`.
      const serverMsg = error.response?.data?.error || error.response?.data?.message;
      throw new Error(serverMsg || error.message || 'Failed to delete farmer');
    }
  }
};

// Analytics API Services
export const analyticsAPI = {
  // Get full analytics overview (totalFarmers, processors, land area, gender/age breakdown, etc.)
  getStats: async () => {
    try {
      const response = await apiClient.get('/analytics/overview');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
    }
  }
  // Other analytics views (municipalities, trends) are computed client-side
  // from the farmers and processors data fetched via farmersAPI and processorsAPI.
};

// Utility API Services
export const utilityAPI = {
  // Get municipalities list
  getMunicipalities: async () => {
    try {
      const response = await apiClient.get('/utilities/municipalities');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch municipalities');
    }
  },

  // Upload file/image
  uploadFile: async (file, type = 'general') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await apiClient.post('/utilities/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'File upload failed');
    }
  }
};

// Team Members API Services
export const teamAPI = {
  // Get all team members
  getAll: async () => {
    try {
      const response = await apiClient.get('/team/getall');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team members');
    }
  }
};

// Services API (platform services offered by admin)
// Helper: map frontend fields to backend fields
const toServerService = (data) => ({
  service_name: data.title ?? data.service_name ?? null,
  service_description: data.description ?? data.service_description ?? null,
  service_contact: data.contact ?? data.service_contact ?? null,
  is_active: data.active !== undefined ? (data.active ? 1 : 0) : (data.is_active !== undefined ? data.is_active : 1),
  created_by: data.created_by ?? null,
});

// Helper: map backend fields to frontend fields
const fromServerService = (row) => ({
  ...row,
  id: row.service_id ?? row.id,
  title: row.service_name ?? row.title ?? '',
  description: row.service_description ?? row.description ?? '',
  contact: row.service_contact ?? row.contact ?? '',
  active: row.is_active !== undefined ? !!row.is_active : (row.active !== undefined ? !!row.active : true),
});

export const servicesAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/services');
      const result = response.data;
      // Normalize rows from server format to frontend format
      if (result?.data && Array.isArray(result.data)) {
        result.data = result.data.map(fromServerService);
      }
      return result;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch services');
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/services/${encodeURIComponent(id)}`);
      const result = response.data;
      if (result?.data) {
        result.data = fromServerService(result.data);
      }
      return result;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch service');
    }
  },

  create: async (serviceData) => {
    try {
      const payload = toServerService(serviceData);
      const response = await apiClient.post('/services', payload);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create service');
    }
  },

  update: async (id, serviceData) => {
    try {
      const payload = toServerService(serviceData);
      const response = await apiClient.put(`/services/${encodeURIComponent(id)}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update service');
    }
  },

  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/services/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete service');
    }
  }
};

export const mapsAPI = {
  getCatalog: async (category) => {
    try {
      const response = await apiClient.get('/maps/catalog', {
        params: category ? { category } : undefined,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch maps');
    }
  },

  getOne: async (category, municipality) => {
    try {
      const response = await apiClient.get(
        `/maps/catalog/${encodeURIComponent(category)}/${encodeURIComponent(municipality)}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch map');
    }
  },

  saveCatalog: async (mapData) => {
    try {
      const response = await apiClient.post('/maps/catalog/save', mapData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to save map');
    }
  },

  deleteCatalog: async (category, id) => {
    try {
      const response = await apiClient.delete(`/maps/catalog/${encodeURIComponent(category)}/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to delete map');
    }
  },
};